import { ErrorCode, TriggerIgnoreReason } from '@bit-box/common';
import type { LocaleResources } from '../types';

export const TriggerIgnoreReasonCaption: LocaleResources['TriggerIgnoreReasonCaption'] = {
  [TriggerIgnoreReason.IGNORED_BY_PRICE_CHANGE]:
    'Ignored because the price change (${changePercent}%) between the last entry price (${lastOpenPrice}) and current price (${price}) is less than ignorePriceChangePercent setting (${ignorePriceChangePercent}%)',
  [TriggerIgnoreReason.IGNORED_BY_ORDER_TYPE]:
    'Preventing restart of position that was manually closed, take-profit, or stop-loss',
};

/**
 * English error messages for ErrorCode
 * {key} placeholders are replaced with ctx values.
 */
export const ErrorMessages: LocaleResources['ErrorMessages'] = {
  // Existing
  [ErrorCode.ERR_FORM_INPUT_ERROR]: 'Invalid input value.',
  [ErrorCode.ERR_POSITION_NOT_FOUND]: '{symbol} position not found in DB for liquidation.',
  [ErrorCode.ERR_FETCH_CANDLES]: 'Failed to fetch {symbol} {timeframe} candles',
  [ErrorCode.ERR_INSUFFICIENT_PROXIES]: 'Insufficient proxy IPs. Required: {requiredCount}, Current: {currentCount}',
  [ErrorCode.ERR_PROXY_INVALID]:
    'Proxy validation failed: {proxyIp} ({message}). Please check the proxy settings and enter a valid proxy IP.',

  // Exchange
  [ErrorCode.ERR_EXCHANGE_NOT_SELECTED]: 'Please select an exchange first.',
  [ErrorCode.ERR_EXCHANGE_CONFIG_NOT_FOUND]: 'Exchange configuration not found',
  [ErrorCode.ERR_EXCHANGE_CONFIG_READ_ERROR]: 'Configuration read error',
  [ErrorCode.ERR_EXCHANGE_INSTANCE_CREATE_ERROR]: 'Exchange instance creation error',
  [ErrorCode.ERR_EXCHANGE_CONNECTION_ERROR]: 'Error occurred while connecting to exchange.',
  [ErrorCode.ERR_POSITION_MODE_CHANGE_FAILED]:
    'Failed to change position mode. Cannot change mode with open positions.',

  // Invest/Session
  [ErrorCode.ERR_INVEST_ALREADY_RUNNING]: 'Already running.',
  [ErrorCode.ERR_INVEST_ALREADY_STOPPED]: 'Already stopped.',
  [ErrorCode.ERR_INVEST_CANNOT_MODIFY_WHILE_RUNNING]: 'Cannot modify while investment is running.',
  [ErrorCode.ERR_INVEST_NO_SYMBOLS]: 'No investment candidate symbols.',
  [ErrorCode.ERR_INVEST_SYMBOL_CHANGING]: 'Symbols are being changed. Please try again later.',
  [ErrorCode.ERR_INVEST_CANNOT_SWITCH_TAB]: 'Cannot switch to/from backtest tab while investing.',
  [ErrorCode.ERR_INVEST_STOPPING]: 'Position entry orders are not allowed while stopping investment.',
  [ErrorCode.ERR_BACKTEST_START_TIME_REQUIRED]: 'Please select a backtest start time first.',
  [ErrorCode.ERR_INVEST_END_TIME_BEFORE_START]: 'Investment end time must be after start time ({startAt}).',
  [ErrorCode.ERR_INVEST_END_TIME_IN_PAST]: 'Investment end time must be in the future.',

  // Position/Order
  [ErrorCode.ERR_POSITION_NOT_EXISTS]: 'Position does not exist.',
  [ErrorCode.ERR_SYMBOL_INFO_NOT_FOUND]: 'Symbol information not found.',
  [ErrorCode.ERR_ENTRY_NOT_ALLOWED]: '{symbol} {posOrderType} entry not allowed',
  [ErrorCode.ERR_ORDER_ALREADY_IN_PROGRESS]: '{symbol} order already in progress',
  [ErrorCode.ERR_MARKET_STATUS_NOT_FOUND]: 'Market status object not found, cancelling',
  [ErrorCode.ERR_CANDLE_SERVICE_NOT_FOUND]: '{symbol} {timeframe} CandleService not found',
  [ErrorCode.ERR_PRICE_NOT_FOUND]: '{symbol} price not found',
  [ErrorCode.ERR_ORDERBOOK_NOT_FOUND]: 'Orderbook data not found',
  [ErrorCode.ERR_NO_TRADABLE_AMOUNT]: 'No tradable amount',
  [ErrorCode.ERR_VIRTUAL_ORDER_FAILED]: '{symbol} virtual order processing failed',
  [ErrorCode.ERR_POSITION_SIZE_NOT_CHANGED]: '{symbol} position size not changed',
  [ErrorCode.ERR_ORDER_CANCELED]: 'Order status: {status}. Order ID: {orderId}',
  [ErrorCode.ERR_SYMBOL_AMOUNT_EMPTY]: 'Symbol amount is empty',

  // Balance
  [ErrorCode.ERR_VIRTUAL_BALANCE_TOO_LOW]: 'Virtual balance must be at least {minBalance}.',
  [ErrorCode.ERR_INVEST_MODE_MISMATCH]: 'Investment mode mismatch.',
  [ErrorCode.ERR_WITHDRAW_WHILE_STOPPED]: 'Cannot withdraw while bot is stopped',
  [ErrorCode.ERR_SEED_REQUIRED]: 'Please set the seed before starting the session.',
  [ErrorCode.ERR_WITHDRAW_EXCEED_MAX]: 'Maximum withdrawable amount is {maxAmount}.',
  [ErrorCode.ERR_COST_BELOW_MIN]: 'Allocated investment is below minimum cost limit ({minCost})',
  [ErrorCode.ERR_COST_EXCEED_MAX]: 'Allocated investment exceeds maximum cost limit ({maxCost})',
  [ErrorCode.ERR_INSUFFICIENT_INVESTABLE]: 'Insufficient investable amount ({investable}). Investment ({quoteAmt})',

  // Strategy
  [ErrorCode.ERR_STRATEGY_CONFIG_READ_ERROR]: 'Configuration read error',
  [ErrorCode.ERR_STRATEGY_LOG_READ_ERROR]: 'Log read error',
  [ErrorCode.ERR_NO_TIMEFRAMES]: 'No indicator timeframes configured.',
  [ErrorCode.ERR_UNSUPPORTED_TRIGGER_TYPE]: '{triggerType} trigger type is not supported.',
  [ErrorCode.ERR_CONDITION_NOT_FOUND]: 'Condition not found. (id={conditionId}, name={conditionName})',
  [ErrorCode.ERR_SIGNAL_TYPE_NOT_FOUND]: 'Signal type #{signalTypeId} not found.',
  [ErrorCode.ERR_INDICATOR_NOT_FOUND]: 'Indicator #{indicatorId} not found.',
  [ErrorCode.ERR_CONDITION_REF_INVALID_INDICATOR]: 'Condition references non-existent indicators: "{indicatorIds}".',
  [ErrorCode.ERR_CONDITION_REF_INVALID_SIGNAL_TYPE]:
    'Condition references non-existent signal types: "{signalTypeIds}".',
  [ErrorCode.ERR_CHANNEL_CONDITION_OPERANDS]: 'Channel comparison condition must have 3 operands.',
  [ErrorCode.ERR_USAGE_MIN_ONE]: '[Usage] must have at least one item.',
  [ErrorCode.ERR_NAME_REQUIRED]: 'Please enter a strategy name',
  [ErrorCode.ERR_NAME_DUPLICATE]: 'Name "{name}" already exists.',
  [ErrorCode.ERR_REQUIRED_SIGNAL_TYPE_MODIFY]: 'Cannot modify required signal type',
  [ErrorCode.ERR_REQUIRED_SIGNAL_TYPE_DELETE]: 'Cannot delete required signal type',
  [ErrorCode.ERR_STRATEGY_OR_ID_REQUIRED]: 'strategyConfig or strategyId is required.',

  // Analysis
  [ErrorCode.ERR_CORRELATION_CALC_CANCELLED]: 'The correlation calculation has been cancelled.',

  // Auth
  [ErrorCode.ERR_ALREADY_LOGGED_IN_OTHER_USER]: 'Already logged in with another user.',
  [ErrorCode.ERR_ALREADY_LOGGED_IN_OTHER_LICENSE]: 'Already logged in with another license.',
  [ErrorCode.ERR_API_KEY_REQUIRED]: 'Please register the exchange API key first.',

  // Misc
  [ErrorCode.ERR_TELEGRAM_API_FAILED]: 'Telegram API call failed',
  [ErrorCode.ERR_TELEGRAM_NO_CHAT]:
    'No chat room found. Please add the bot to a Telegram chat and enter the /start command.',
  [ErrorCode.ERR_TELEGRAM_NO_TOKEN]: 'Telegram API token is empty.',
  [ErrorCode.ERR_MUTEX_ALREADY_LOCKED]: 'Already locked.',
  [ErrorCode.ERR_CANDLE_FETCH_MAIN_ONLY]: 'Candle fetch failed. Candle fetching is only available in the main process.',
  [ErrorCode.ERR_INDICATOR_BUFFER_INSUFFICIENT]: 'Indicator calculation failed due to insufficient buffer candles',
  [ErrorCode.ERR_TALIB_FUNCTION_NOT_FOUND]: 'Technical analysis function not found: {funcId}',
  [ErrorCode.ERR_EXCHANGE_SERVICE_NOT_INIT]: 'Exchange service not initialized.',
  [ErrorCode.ERR_IMMEDIATE_LIQUIDATION_RISK]:
    'Immediate liquidation risk. Mark price: {markPrice}, Liquidation price: {liqPrice}',
  [ErrorCode.ERR_CONDITION_SYMBOL_REQUIRED]: 'A symbol is required for conditions referencing shapes or webhooks.',
};
